import { produce } from "immer"
import type { FormAttachment } from "../../components/organisms"
import type { Attachment } from "../projects/projects.types"
import {
  ATTACHMENT_EDITOR_CHANGE_ATTACHMENT,
  ATTACHMENT_EDITOR_CLOSE_DIALOG,
  ATTACHMENT_EDITOR_FINISH_CREATE,
  ATTACHMENT_EDITOR_FINISH_PIN_POSITION_UPDATE,
  ATTACHMENT_EDITOR_HIGHLIGHT_ATTACHMENT,
  ATTACHMENT_EDITOR_MOVE_PIN,
  ATTACHMENT_EDITOR_OPEN_DIALOG,
  ATTACHMENT_EDITOR_SAVE_FORM,
  ATTACHMENT_EDITOR_SELECT_ATTACHMENT,
  ATTACHMENT_EDITOR_SELECT_LOCATION,
  ATTACHMENT_EDITOR_SELECT_TYPE,
  ATTACHMENT_EDITOR_START_CREATE,
  ATTACHMENT_EDITOR_START_EDIT,
  ATTACHMENT_EDITOR_START_UPDATE,
  ATTACHMENT_EDITOR_UPDATE_PIN_POSITION
} from "./attachment-editor.actions"
import type { AttachmentEditorActions, IAttachmentEditorState } from "./attachment-editor.types"

const attachmentEditorInitialState: IAttachmentEditorState = {
  dialogOpen: false,
  isCreating: false
}

export function attachmentEditorReducer(
  currentState: IAttachmentEditorState | undefined,
  action: AttachmentEditorActions
): IAttachmentEditorState {
  const state = currentState ?? attachmentEditorInitialState
  switch (action.type) {
    case ATTACHMENT_EDITOR_START_CREATE:
      return startCreate(state, action.payload as FormAttachment)
    case ATTACHMENT_EDITOR_START_EDIT:
      return startEdit(state, action.payload as FormAttachment)
    case ATTACHMENT_EDITOR_SELECT_TYPE:
      return selectType(state, action.payload as Attachment)
    case ATTACHMENT_EDITOR_SAVE_FORM:
      return saveForm(state, action.payload as Attachment)
    case ATTACHMENT_EDITOR_SELECT_LOCATION:
      return selectLocation(state, action.payload as Attachment)
    case ATTACHMENT_EDITOR_FINISH_CREATE:
      return finishCreate(state)
    case ATTACHMENT_EDITOR_START_UPDATE:
      return setCurrentAttachment(state, action.payload as Attachment)
    case ATTACHMENT_EDITOR_CHANGE_ATTACHMENT:
      return setCurrentAttachment(state, action.payload as Attachment)
    case ATTACHMENT_EDITOR_OPEN_DIALOG:
      return changeDialogState(state, true)
    case ATTACHMENT_EDITOR_CLOSE_DIALOG:
      return changeDialogState(state, false)
    case ATTACHMENT_EDITOR_MOVE_PIN:
      return startPinMove(state, action.payload as Attachment)
    case ATTACHMENT_EDITOR_UPDATE_PIN_POSITION:
      return setCurrentAttachment(state, action.payload as Attachment)
    case ATTACHMENT_EDITOR_FINISH_PIN_POSITION_UPDATE:
      return finishPinMove(state)
    case ATTACHMENT_EDITOR_SELECT_ATTACHMENT:
      return setCurrentAttachment(state, action.payload as Attachment)
    case ATTACHMENT_EDITOR_HIGHLIGHT_ATTACHMENT:
      return setHighlightAttachment(state, action.payload as Attachment)
    default:
      return state
  }
}

function setCurrentAttachment(currentState: IAttachmentEditorState, attachment: Attachment): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.currentAttachment = attachment
  })
}

function changeDialogState(currentState: IAttachmentEditorState, open: boolean): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.dialogOpen = open
  })
}

function startCreate(currentState: IAttachmentEditorState, attachment: FormAttachment): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.dialogOpen = true
    draftState.currentlyEditing = attachment
    draftState.isCreating = true
  })
}

function startEdit(currentState: IAttachmentEditorState, attachment: FormAttachment): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.dialogOpen = true
    draftState.currentlyEditing = attachment
    draftState.isCreating = false
  })
}

function selectType(currentState: IAttachmentEditorState, attachment: Attachment): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.currentAttachment = attachment
  })
}

function saveForm(currentState: IAttachmentEditorState, attachment: Attachment): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.currentAttachment = attachment
    draftState.dialogOpen = false
  })
}

function selectLocation(currentState: IAttachmentEditorState, attachment: Attachment): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.currentAttachment = attachment
    draftState.dialogOpen = true
  })
}

function finishCreate(currentState: IAttachmentEditorState): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.dialogOpen = false
    draftState.currentAttachment = undefined
  })
}

function startPinMove(currentState: IAttachmentEditorState, attachment: Attachment): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.currentAttachment = attachment
  })
}

function finishPinMove(currentState: IAttachmentEditorState): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.currentAttachment = undefined
  })
}

function setHighlightAttachment(currentState: IAttachmentEditorState, attachment: Attachment): IAttachmentEditorState {
  return produce(currentState, (draftState: IAttachmentEditorState) => {
    draftState.highlightAttachment = attachment
  })
}
